import { QueryClient, QueryCache, MutationCache } from "@tanstack/react-query";
import { ApiRequestError } from "./api/client";
import { getErrorMessage } from "./api/errorMessage";
import { notify } from "./notify";

/** The server refused a request because its module is off, so the tab's copy of
 * the features is out of date: re-read it and the sidebar and page follow. */
function reloadFeaturesIfModuleOff(error: unknown) {
  if (error instanceof ApiRequestError && error.appError.code === "FEATURE_DISABLED") {
    void queryClient.invalidateQueries({ queryKey: qk.features.my });
  }
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      // /features/my is itself gated: re-reading it after its own refusal
      // would never stop.
      if (query.queryKey[0] !== qk.features.my[0]) reloadFeaturesIfModuleOff(error);
    },
  }),
  // Every mutation failure app-wide is toasted from this one place, so a
  // new feature gets error feedback for free instead of needing its own
  // onError boilerplate. Deliberately scoped to mutations, not queries -
  // background GET failures happen for benign reasons (disabled/conditional
  // queries, unmount races) and already have their own inline ErrorMessage
  // display convention; toasting every one of those would be noise, not a
  // fix. A mutation can still opt out via `meta: { silent: true }` for the
  // rare case where it needs to handle its own failure UI entirely.
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      reloadFeaturesIfModuleOff(error);
      if (mutation.meta?.silent) return;
      // A fatal 401 already got its own toast directly from the axios
      // interceptor (lib/api/client.ts) - it fires there, not here, so it
      // reliably shows up even when the request that triggered it was a
      // background query rather than a mutation. Showing it again here
      // would just double it up for the mutation case.
      if (error instanceof ApiRequestError && error.status === 401) return;
      notify.error(getErrorMessage(error));
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: (failureCount, error) => {
        // A 4xx is never worth retrying - it will fail identically the second
        // time. Only transient network/5xx errors get the default 1 retry.
        if (error instanceof ApiRequestError && error.status && error.status < 500) return false;
        return failureCount < 1;
      },
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: false,
    },
  },
});

/** Query key factory, kept centralised so an invalidation in one feature
 * (e.g. marking attendance) can target another feature's cache (the
 * dashboard's attendance mix) without guessing string literals. */
export const qk = {
  me: ["auth", "me"] as const,
  sessions: ["auth", "sessions"] as const,
  loginActivity: (filters: unknown) => ["auth", "loginActivity", filters] as const,
  employees: {
    list: (filters: unknown) => ["employees", "list", filters] as const,
    archived: (filters: unknown) => ["employees", "archived", filters] as const,
    detail: (id: string) => ["employees", "detail", id] as const,
    headcount: ["employees", "headcount"] as const,
    upcomingEvents: (params: unknown) => ["employees", "upcomingEvents", params] as const,
    me: ["employees", "me"] as const,
    nextCode: ["employees", "nextCode"] as const,
    branchHistory: (id: string) => ["employees", "branchHistory", id] as const,
    departmentHistory: (id: string) => ["employees", "departmentHistory", id] as const,
    managerHistory: (id: string) => ["employees", "managerHistory", id] as const,
  },
  departments: {
    list: (filters: unknown) => ["departments", "list", filters] as const,
    detail: (id: string) => ["departments", "detail", id] as const,
    summary: ["departments", "summary"] as const,
    hierarchy: (id: string) => ["departments", "hierarchy", id] as const,
    employees: (id: string) => ["departments", "employees", id] as const,
    managers: (id: string) => ["departments", "managers", id] as const,
    history: (id: string) => ["departments", "history", id] as const,
    myTeams: ["departments", "myTeams"] as const,
    assignments: (departmentId: string) => ["departments", "assignments", departmentId] as const,
    designations: (departmentId: string, filters: unknown) => ["departments", "designations", departmentId, filters] as const,
  },
  branches: {
    list: (filters: unknown) => ["branches", "list", filters] as const,
    detail: (id: string) => ["branches", "detail", id] as const,
    summary: ["branches", "summary"] as const,
    geoFences: (branchId: string) => ["branches", "geoFences", branchId] as const,
    departments: (branchId: string, filters: unknown) => ["branches", "departments", branchId, filters] as const,
    headcount: ["branches", "headcount"] as const,
    assignments: (branchId: string) => ["branches", "assignments", branchId] as const,
  },
  geoFences: {
    list: (filters: unknown) => ["geoFences", "list", filters] as const,
    detail: (id: string) => ["geoFences", "detail", id] as const,
  },
  attendance: {
    list: (filters: unknown) => ["attendance", "list", filters] as const,
    exceptions: (filters: unknown) => ["attendance", "exceptions", filters] as const,
    summary: (period?: string) => ["attendance", "summary", period] as const,
    dailySummary: (date: string) => ["attendance", "dailySummary", date] as const,
    rangeSummary: (filters: unknown) => ["attendance", "rangeSummary", filters] as const,
    employee: (id: string, from?: string, to?: string) => ["attendance", "employee", id, from, to] as const,
    logs: (filters: unknown) => ["attendance", "logs", filters] as const,
    unmapped: (params: unknown) => ["attendance", "unmapped", params] as const,
    myPunches: (filters: unknown) => ["attendance", "myPunches", filters] as const,
    // Field Workforce. The listing key is prefixed ["attendance",
    // "fieldWorkforce"] so a mode change can invalidate every page of it at
    // once - who appears in that list is exactly what an employee's mode
    // decides.
    fieldWorkforce: (params: unknown) => ["attendance", "fieldWorkforce", params] as const,
    fieldActivity: (employeeId: string, from?: string, to?: string) =>
      ["attendance", "fieldActivity", employeeId, from, to] as const,
    myFieldActivity: (from?: string, to?: string) => ["attendance", "myFieldActivity", from, to] as const,
    employeeMode: (employeeId: string) => ["attendance", "employeeMode", employeeId] as const,
    // Attendance Mode management screen. All under ["attendance", "modes"]
    // so any mode change can invalidate the whole screen in one call.
    modeOverview: ["attendance", "modes", "overview"] as const,
    modeEmployees: (filters: unknown) => ["attendance", "modes", "employees", filters] as const,
    modeHistory: (page: number) => ["attendance", "modes", "history", page] as const,
  },
  // Employee Location Tracking (admin monitoring views). Every key is
  // prefixed ["location"] so the enable/disable toggle can invalidate one
  // employee's whole location surface - state, history and sessions - in a
  // single call, the same way an attendance-mode change invalidates
  // ["attendance","fieldWorkforce"] above.
  location: {
    state: (employeeId: string) => ["location", "state", employeeId] as const,
    config: (employeeId: string) => ["location", "config", employeeId] as const,
    history: (employeeId: string, filters: unknown) => ["location", "history", employeeId, filters] as const,
    sessions: (employeeId: string, from?: string, to?: string) =>
      ["location", "sessions", employeeId, from, to] as const,
    overview: (filters: unknown) => ["location", "overview", filters] as const,
    timeline: (employeeId: string, params: unknown) => ["location", "timeline", employeeId, params] as const,
    summary: (employeeId: string, params: unknown) => ["location", "summary", employeeId, params] as const,
  },
  // Tenant dashboard layout (Settings > Dashboard): catalog + saved layout.
  dashboardLayout: ["dashboardLayout"] as const,
  earnings: {
    mine: (period: string) => ["earnings", "mine", period] as const,
    employee: (employeeId: string, period: string) => ["earnings", "employee", employeeId, period] as const,
    settings: ["earnings", "settings"] as const,
    payday: ["earnings", "payday"] as const,
    wallet: ["earnings", "wallet"] as const,
    achievements: ["earnings", "achievements"] as const,
    recognition: ["earnings", "recognition"] as const,
  },
  payroll: {
    runs: (filters: unknown) => ["payroll", "runs", filters] as const,
    run: (id: string) => ["payroll", "run", id] as const,
    runByPeriod: (period: string) => ["payroll", "runByPeriod", period] as const,
    payslips: (filters: unknown) => ["payroll", "payslips", filters] as const,
    payslip: (id: string) => ["payroll", "payslip", id] as const,
    myPayslips: ["payroll", "myPayslips"] as const,
    approvalHistory: (id: string) => ["payroll", "approvalHistory", id] as const,
    teamSummary: (period: string) => ["payroll", "teamSummary", period] as const,
    payments: {
      batches: (filters: unknown) => ["payroll", "payments", "batches", filters] as const,
      batch: (id: string) => ["payroll", "payments", "batch", id] as const,
      items: (filters: unknown) => ["payroll", "payments", "items", filters] as const,
    },
  },
  settings: {
    company: ["settings", "company"] as const,
    attendance: ["settings", "attendance"] as const,
    salary: ["settings", "salary"] as const,
    salaryVersions: ["settings", "salaryVersions"] as const,
    notifications: ["settings", "notifications"] as const,
    emailSender: ["settings", "emailSender"] as const,
    emailDeliveries: ["settings", "emailSender", "deliveries"] as const,
    payslipEmailPreview: (subject: string, body: string) => ["settings", "emailSender", "payslipPreview", subject, body] as const,
    setup: ["settings", "setup"] as const,
  },
  users: {
    list: (filters: unknown) => ["users", "list", filters] as const,
    roles: ["users", "roles"] as const,
    // Custom role management. Deliberately nested under "users" so invalidating
    // ["users"] after a role change also refreshes the assignable-role pickers
    // on the Users page, which read the same backend data.
    customRoles: ["users", "roles", "custom"] as const,
    customRole: (id: string) => ["users", "roles", "custom", id] as const,
    permissionCatalog: ["users", "roles", "permission-catalog"] as const,
  },
  holidays: {
    list: (year: number, params: unknown) => ["holidays", "list", year, params] as const,
    mine: (year: number) => ["holidays", "mine", year] as const,
  },
  leaves: {
    list: (filters: unknown) => ["leaves", "list", filters] as const,
    // Keyed on the whole prospective payload (not just employee/type) so the
    // Apply modal's preview refetches whenever the mode/dates/half-day
    // selection actually changes.
    preview: (input: unknown) => ["leaves", "preview", input] as const,
  },
  leaveTypes: {
    list: (filters: unknown) => ["leaveTypes", "list", filters] as const,
    detail: (id: string) => ["leaveTypes", "detail", id] as const,
  },
  leavePolicies: {
    detail: (leaveTypeId: string) => ["leavePolicies", "detail", leaveTypeId] as const,
  },
  leaveLimits: {
    list: (filters: unknown) => ["leaveLimits", "list", filters] as const,
    detail: (id: string) => ["leaveLimits", "detail", id] as const,
    forEmployee: (employeeId: string) => ["leaveLimits", "employee", employeeId] as const,
  },
  leaveSchedules: {
    list: (filters: unknown) => ["leaveSchedules", "list", filters] as const,
    detail: (id: string) => ["leaveSchedules", "detail", id] as const,
  },
  leaveBalances: {
    list: (employeeId: string | undefined, year: number | undefined) =>
      ["leaveBalances", "list", employeeId, year] as const,
    self: ["leaveBalances", "self"] as const,
  },
  leaveEncashments: {
    list: (filters: unknown) => ["leaveEncashments", "list", filters] as const,
    detail: (id: string) => ["leaveEncashments", "detail", id] as const,
  },
  onboarding: {
    detail: (employeeId: string) => ["onboarding", "detail", employeeId] as const,
    my: ["onboarding", "my"] as const,
    list: (filters: unknown) => ["onboarding", "list", filters] as const,
  },
  onboardingTemplates: {
    list: (includeInactive: boolean) => ["onboardingTemplates", "list", includeInactive] as const,
  },
  probation: {
    detail: (employeeId: string) => ["probation", "detail", employeeId] as const,
    list: (filters: unknown) => ["probation", "list", filters] as const,
    stats: ["probation", "stats"] as const,
    extensions: (id: string) => ["probation", "extensions", id] as const,
    reviews: (id: string) => ["probation", "reviews", id] as const,
  },
  policies: {
    list: (includeInactive: boolean) => ["policies", "list", includeInactive] as const,
    detail: (id: string) => ["policies", "detail", id] as const,
    myPending: ["policies", "myPending"] as const,
    myAcknowledgements: ["policies", "myAcknowledgements"] as const,
  },
  separationPolicy: ["separationPolicy"] as const,
  resignations: {
    list: (filters: unknown) => ["resignations", "list", filters] as const,
    detail: (id: string) => ["resignations", "detail", id] as const,
  },
  separations: {
    list: (filters: unknown) => ["separations", "list", filters] as const,
    detail: (id: string) => ["separations", "detail", id] as const,
    exitInterview: (id: string) => ["separations", "exitInterview", id] as const,
    exitInterviewAnalytics: ["separations", "exitInterviewAnalytics"] as const,
    dashboard: ["separations", "dashboard"] as const,
    myExit: ["separations", "myExit"] as const,
  },
  finalSettlements: {
    list: (filters: unknown) => ["finalSettlements", "list", filters] as const,
    detail: (id: string) => ["finalSettlements", "detail", id] as const,
    bySeparation: (separationId: string) => ["finalSettlements", "bySeparation", separationId] as const,
    revisions: (id: string) => ["finalSettlements", "revisions", id] as const,
    myFinalSettlement: ["finalSettlements", "myFinalSettlement"] as const,
  },
  exitDocumentTemplates: {
    list: (includeInactive: boolean) => ["exitDocumentTemplates", "list", includeInactive] as const,
  },
  integrations: {
    list: ["integrations", "list"] as const,
    jobs: (filters: unknown) => ["integrations", "jobs", filters] as const,
  },
  whatsapp: {
    status: ["whatsapp", "status"] as const,
  },
  teams: {
    status: ["teams", "status"] as const,
    userMappings: (params: unknown) => ["teams", "user-mappings", params] as const,
    notificationSettings: ["teams", "notification-settings"] as const,
    deliveries: (filters: unknown) => ["teams", "deliveries", filters] as const,
  },
  biometric: {
    devices: ["biometric", "devices"] as const,
    webauthnDevices: ["biometric", "webauthn", "devices"] as const,
    logs: (filters: unknown) => ["biometric", "logs", filters] as const,
    syncJobs: (filters: unknown) => ["biometric", "sync", "jobs", filters] as const,
    syncJob: (id: string) => ["biometric", "sync", "jobs", id] as const,
    punches: (filters: unknown) => ["biometric", "punches", filters] as const,
    punch: (id: string) => ["biometric", "punches", id] as const,
    myPunches: (filters: unknown) => ["biometric", "punches", "self", filters] as const,
    exceptions: (filters: unknown) => ["biometric", "exceptions", filters] as const,
    exception: (id: string) => ["biometric", "exceptions", id] as const,
    dailyPunchReport: (filters: unknown) => ["biometric", "reports", "daily", filters] as const,
    monthlyPunchReport: (filters: unknown) => ["biometric", "reports", "monthly", filters] as const,
    missingPunchReport: (filters: unknown) => ["biometric", "reports", "missing-punch", filters] as const,
    exceptionReport: (filters: unknown) => ["biometric", "reports", "exceptions", filters] as const,
    gateways: ["biometric", "gateways"] as const,
    gatewayDevices: (id: string) => ["biometric", "gateways", id, "devices"] as const,
    gatewayAuthLogs: (id: string, filters: unknown) => ["biometric", "gateways", id, "auth-logs", filters] as const,
  },
  reports: {
    attendance: (period: string, deptId?: string) => ["reports", "attendance", period, deptId] as const,
    leave: (period: string, deptId?: string) => ["reports", "leave", period, deptId] as const,
    payroll: (period: string) => ["reports", "payroll", period] as const,
    late: (period: string) => ["reports", "late", period] as const,
    overtime: (period: string) => ["reports", "overtime", period] as const,
    summary: (period: string) => ["reports", "summary", period] as const,
    dashboard: ["reports", "dashboard"] as const,
    geoFenceVerification: (period: string, branchId?: string) =>
      ["reports", "geoFenceVerification", period, branchId] as const,
    geoFenceSummary: ["reports", "geoFenceSummary"] as const,
    // Field Work Reporting (Phase 4)
    fieldWorkReport: (period: string, departmentId?: string) =>
      ["reports", "fieldWorkReport", period, departmentId] as const,
    fieldWorkSummary: (period: string) => ["reports", "fieldWorkSummary", period] as const,
    // ---- Payroll Reporting Phase 3 ----
    payrollSummary: (period: string) => ["reports", "payrollSummary", period] as const,
    payrollDepartment: (period: string) => ["reports", "payrollDepartment", period] as const,
    payrollEarnings: (period: string) => ["reports", "payrollEarnings", period] as const,
    payrollDeductions: (period: string) => ["reports", "payrollDeductions", period] as const,
    payrollNetPay: (period: string) => ["reports", "payrollNetPay", period] as const,
    payrollStatutory: (period: string, type: string) => ["reports", "payrollStatutory", period, type] as const,
    payrollLOP: (period: string) => ["reports", "payrollLOP", period] as const,
    payrollVariance: (period: string, comparePeriod?: string) =>
      ["reports", "payrollVariance", period, comparePeriod] as const,
    payrollSalaryHistory: (employeeId: string, limit?: number) =>
      ["reports", "payrollSalaryHistory", employeeId, limit] as const,
    payrollFnf: (from: string, to: string, status?: string) =>
      ["reports", "payrollFnf", from, to, status] as const,
    payrollPayments: (year: number, month?: number) => ["reports", "payrollPayments", year, month] as const,
  },
  tenants: {
    list: (filters: unknown) => ["tenants", "list", filters] as const,
    detail: (id: string) => ["tenants", "detail", id] as const,
    biometricSummary: ["tenants", "biometricSummary"] as const,
  },
  platformSimulator: {
    devices: ["platformSimulator", "devices"] as const,
    events: (deviceId: string) => ["platformSimulator", "events", deviceId] as const,
  },
  audit: {
    list: (filters: unknown) => ["audit", "list", filters] as const,
    tenantList: (tenantId: string, filters: unknown) => ["audit", "tenantList", tenantId, filters] as const,
  },
  workspaceAccess: {
    list: (filters: unknown) => ["workspaceAccess", "list", filters] as const,
  },
  platformAdmins: {
    list: (filters: unknown) => ["platformAdmins", "list", filters] as const,
    grants: (filters: unknown) => ["platformAdmins", "grants", filters] as const,
  },
  superAdminKeys: {
    list: (filters: unknown) => ["superAdminKeys", "list", filters] as const,
  },
  tempRoleAccess: {
    list: (filters: unknown) => ["tempRoleAccess", "list", filters] as const,
    roles: ["tempRoleAccess", "roles"] as const,
  },
  mfa: {
    status: ["mfa", "status"] as const,
  },
  platformBilling: {
    overview: ["platformBilling", "overview"] as const,
    detail: (tenantId: string) => ["platformBilling", "detail", tenantId] as const,
  },
  billing: {
    plans: ["billing", "plans"] as const,
    subscription: ["billing", "subscription"] as const,
    invoices: (filters: unknown) => ["billing", "invoices", filters] as const,
  },
  compliance: {
    summary: ["compliance", "summary"] as const,
    settings: ["compliance", "settings"] as const,
    filings: (filters: unknown) => ["compliance", "filings", filters] as const,
    declarations: (filters: unknown) => ["compliance", "declarations", filters] as const,
    employeeInfo: (id: string) => ["compliance", "employeeInfo", id] as const,
    myStatutoryInfo: ["compliance", "myStatutoryInfo"] as const,
    myAnnualStatement: (fy?: string) => ["compliance", "myAnnualStatement", fy] as const,
    myTaxDeclaration: (fy?: string) => ["compliance", "myTaxDeclaration", fy] as const,
    taxComputation: (employeeId: string, fy: string) => ["compliance", "taxComputation", employeeId, fy] as const,
    myTaxComputation: (fy: string) => ["compliance", "myTaxComputation", fy] as const,
  },
  engage: {
    feed: ["engage", "feed"] as const,
    badges: ["engage", "badges"] as const,
    comments: (id: string) => ["engage", "comments", id] as const,
    mySurveys: ["engage", "my-surveys"] as const,
    mySurvey: (id: string) => ["engage", "my-survey", id] as const,
    surveys: ["engage", "surveys"] as const,
    results: (id: string) => ["engage", "results", id] as const,
  },
  developer: {
    catalog: ["developer", "catalog"] as const,
    keys: ["developer", "keys"] as const,
    webhooks: ["developer", "webhooks"] as const,
    deliveries: (id: string) => ["developer", "deliveries", id] as const,
  },
  skills: {
    catalog: ["skills", "catalog"] as const,
    mine: ["skills", "mine"] as const,
    matrix: ["skills", "matrix"] as const,
    gaps: ["skills", "gaps"] as const,
    search: (skillId: string, min: number) => ["skills", "search", skillId, min] as const,
    requirements: ["skills", "requirements"] as const,
  },
  okrs: {
    board: (period: string) => ["okrs", "board", period] as const,
    checkins: (krId: string) => ["okrs", "checkins", krId] as const,
  },
  oneOnOnes: {
    list: ["one-on-ones", "list"] as const,
    partners: ["one-on-ones", "partners"] as const,
    detail: (id: string) => ["one-on-ones", "detail", id] as const,
  },
  benefits: {
    components: ["benefits", "components"] as const,
    plans: (fy: string) => ["benefits", "plans", fy] as const,
    claims: (fy: string, status: string) => ["benefits", "claims", fy, status] as const,
    mine: (fy: string) => ["benefits", "mine", fy] as const,
    employee: (id: string, fy: string) => ["benefits", "employee", id, fy] as const,
  },
  reportBuilder: {
    sources: ["report-builder", "sources"] as const,
    saved: ["report-builder", "saved"] as const,
  },
  customFields: {
    defs: ["custom-fields", "defs"] as const,
    employee: (id: string) => ["custom-fields", "employee", id] as const,
    mine: ["custom-fields", "mine"] as const,
    forms: ["forms", "manage"] as const,
    form: (id: string) => ["forms", "form", id] as const,
    submissions: (id: string) => ["forms", "submissions", id] as const,
    myForms: ["forms", "mine"] as const,
    myForm: (id: string) => ["forms", "my-form", id] as const,
  },
  timesheets: {
    myWeek: (start: string) => ["timesheets", "my-week", start] as const,
    approvals: (status: string) => ["timesheets", "approvals", status] as const,
    week: (id: string) => ["timesheets", "week", id] as const,
    projects: ["timesheets", "projects"] as const,
    report: (from: string, to: string, group: string) => ["timesheets", "report", from, to, group] as const,
  },
  letters: {
    list: (filters: unknown) => ["letters", "list", filters] as const,
    templates: ["letters", "templates"] as const,
    variables: ["letters", "variables"] as const,
    mine: ["letters", "mine"] as const,
    requestable: ["letters", "requestable"] as const,
  },
  expense: {
    summary: ["expense", "summary"] as const,
    my: (filters: unknown) => ["expense", "my", filters] as const,
    team: (filters: unknown) => ["expense", "team", filters] as const,
    reports: (filters: unknown) => ["expense", "reports", filters] as const,
    approved: (filters: unknown) => ["expense", "approved", filters] as const,
    detail: (id: string) => ["expense", "detail", id] as const,
    categories: (filters: unknown) => ["expense", "categories", filters] as const,
    policies: ["expense", "policies"] as const,
  },
  bonus: {
    list: (filters: unknown) => ["bonus", "list", filters] as const,
    detail: (id: string) => ["bonus", "detail", id] as const,
  },
  // Finance Admin dashboard/approvals/reconciliation - aggregates over
  // payroll/expense/salaryAdvance/bonus above, so a mutation on any of those
  // (approve a payroll run, mark an expense paid) invalidates this
  // namespace too rather than just its own. See lib/api/finance.ts.
  // HR Employee's landing page - see lib/api/hrDashboard.ts.
  hrDashboard: ["hrDashboard"] as const,
  finance: {
    dashboard: ["finance", "dashboard"] as const,
    approvals: ["finance", "approvals"] as const,
    reconciliation: (params: unknown) => ["finance", "reconciliation", params] as const,
    reconciliationHistory: (id: string) => ["finance", "reconciliationHistory", id] as const,
  },
  budgets: {
    list: (filters: unknown) => ["budgets", "list", filters] as const,
    detail: (id: string) => ["budgets", "detail", id] as const,
    utilization: (id: string) => ["budgets", "utilization", id] as const,
  },
  shifts: {
    list: (filters: unknown) => ["shifts", "list", filters] as const,
    detail: (id: string) => ["shifts", "detail", id] as const,
    assignments: (employeeId: string) => ["shifts", "assignments", employeeId] as const,
    roster: (filters: unknown) => ["shifts", "roster", filters] as const,
    resolve: (params: unknown) => ["shifts", "resolve", params] as const,
    // Department/branch-wise shift assignment (migration 0167).
    departmentAssignments: (params: unknown) => ["shifts", "departmentAssignments", params] as const,
  },
  workSchedules: {
    list: (filters: unknown) => ["workSchedules", "list", filters] as const,
    detail: (id: string) => ["workSchedules", "detail", id] as const,
    assignments: (page: unknown) => ["workSchedules", "assignments", page] as const,
    roster: (filters: unknown) => ["workSchedules", "roster", filters] as const,
  },
  salaryAdvance: {
    list: (filters: unknown) => ["salaryAdvance", "list", filters] as const,
    detail: (id: string) => ["salaryAdvance", "detail", id] as const,
    policy: ["salaryAdvance", "policy"] as const,
    eligibility: ["salaryAdvance", "eligibility"] as const,
    availability: ["salaryAdvance", "availability"] as const,
  },
  // New Overtime Tracking and Approval module - deliberately its own
  // namespace, distinct from the pre-existing `reports.overtime` above
  // (that one keys GET /reports/overtime, the older automatic-OT report
  // unrelated to this module; see OvertimeReportsPage.tsx's own note).
  overtime: {
    list: (filters: unknown) => ["overtime", "list", filters] as const,
    detail: (id: string) => ["overtime", "detail", id] as const,
    reportSummary: (filters: unknown) => ["overtime", "reportSummary", filters] as const,
    policy: ["overtime", "policy"] as const,
  },
  employeeDetails: {
    catalog: ["employeeDetails", "catalog"] as const,
    channels: ["employeeDetails", "channels"] as const,
    templates: (filters: unknown) => ["employeeDetails", "templates", filters] as const,
    template: (id: string) => ["employeeDetails", "template", id] as const,
    requests: (filters: unknown) => ["employeeDetails", "requests", filters] as const,
    request: (id: string) => ["employeeDetails", "request", id] as const,
    summary: ["employeeDetails", "summary"] as const,
    batches: (filters: unknown) => ["employeeDetails", "batches", filters] as const,
  },
  // Google Forms integration - Employee Details Collection's only
  // employee-facing form since the Google-only pivot. See lib/api/google.ts.
  google: {
    status: ["google", "status"] as const,
  },
  employeeRequests: {
    list: (filters: unknown) => ["employeeRequests", "list", filters] as const,
    detail: (id: string) => ["employeeRequests", "detail", id] as const,
  },
  combinedAccess: {
    list: (filters: unknown) => ["combinedAccess", "list", filters] as const,
    eligibility: ["combinedAccess", "eligibility"] as const,
    settings: ["combinedAccess", "settings"] as const,
    permissionsFor: (userId: string) => ["combinedAccess", "permissions", userId] as const,
  },
  document: {
    list: (filters: unknown) => ["document", "list", filters] as const,
    detail: (id: string) => ["document", "detail", id] as const,
    versions: (id: string) => ["document", "versions", id] as const,
    auditLog: (id: string, filters: unknown) => ["document", "auditLog", id, filters] as const,
    categories: (filters: unknown) => ["document", "categories", filters] as const,
    dashboard: ["document", "dashboard"] as const,
    myTasks: ["document", "myTasks"] as const,
    tenantAuditLog: (filters: unknown) => ["document", "tenantAuditLog", filters] as const,
    requiredDocuments: (employeeId: string) => ["document", "requiredDocuments", employeeId] as const,
    reportPendingVerification: ["document", "report", "pendingVerification"] as const,
    reportRejected: ["document", "report", "rejected"] as const,
    reportExpiring: (withinDays?: number) => ["document", "report", "expiring", withinDays] as const,
    reportAudit: ["document", "report", "audit"] as const,
    reportMissing: ["document", "report", "missing"] as const,
    reportDepartmentCompliance: ["document", "report", "departmentCompliance"] as const,
    reportEmployeeCompliance: ["document", "report", "employeeCompliance"] as const,
  },
  documentTemplates: {
    list: (filters: unknown) => ["documentTemplates", "list", filters] as const,
    detail: (id: string) => ["documentTemplates", "detail", id] as const,
    compliance: (filters: unknown) => ["documentTemplates", "compliance", filters] as const,
  },
  documentRequests: {
    list: (filters: unknown) => ["documentRequests", "list", filters] as const,
    detail: (id: string) => ["documentRequests", "detail", id] as const,
  },
  notifications: {
    list: (filters: unknown) => ["notifications", "list", filters] as const,
    unreadCount: ["notifications", "unreadCount"] as const,
  },
  announcements: {
    list: (filters: unknown) => ["announcements", "list", filters] as const,
    detail: (id: string) => ["announcements", "detail", id] as const,
    unreadCount: ["announcements", "unreadCount"] as const,
  },
  features: {
    my: ["features", "my"] as const,
    modules: ["features", "modules"] as const,
    tenant: (tenantId: string) => ["features", "tenant", tenantId] as const,
  },
  tracing: {
    // Only consumer left: the Super-Admin-entered-workspace "Tenant Tracing
    // Logs"/"Tenant Health Monitoring" pages - see lib/api/tracing.ts. The
    // tenant id is part of both keys so switching workspaces (Tenant A ->
    // Tenant B) never renders a cached response from the previous tenant.
    services: ["tracing", "services"] as const,
    traces: (filters: unknown) => ["tracing", "traces", filters] as const,
    health: (tenantId: string) => ["tracing", "health", tenantId] as const,
    traceDetail: (tenantId: string, traceId: string) => ["tracing", "traceDetail", tenantId, traceId] as const,
  },
  // Tracing Logs - the genuinely tenant-scoped page, see lib/api/observability.ts.
  observability: {
    traces: (filters: unknown) => ["observability", "traces", filters] as const,
    traceDetail: (traceId: string) => ["observability", "traceDetail", traceId] as const,
  },
  recruitment: {
    requisitions: (filters: unknown) => ["recruitment", "requisitions", filters] as const,
    requisition: (id: string) => ["recruitment", "requisition", id] as const,
    requisitionApprovals: (id: string) => ["recruitment", "requisitionApprovals", id] as const,
    candidates: (filters: unknown) => ["recruitment", "candidates", filters] as const,
    candidate: (id: string) => ["recruitment", "candidate", id] as const,
    interviews: (filters: unknown) => ["recruitment", "interviews", filters] as const,
    interview: (id: string) => ["recruitment", "interview", id] as const,
    assessments: (filters: unknown) => ["recruitment", "assessments", filters] as const,
    assessment: (id: string) => ["recruitment", "assessment", id] as const,
    offers: (filters: unknown) => ["recruitment", "offers", filters] as const,
    offer: (id: string) => ["recruitment", "offer", id] as const,
    offerApprovals: (id: string) => ["recruitment", "offerApprovals", id] as const,
    myOffer: ["recruitment", "myOffer"] as const,
    dashboard: ["recruitment", "dashboard"] as const,
    dashboardFiltered: (filters: unknown) => ["recruitment", "dashboard", filters] as const,
    report: (filters: unknown) => ["recruitment", "report", filters] as const,
  },
  assets: {
    categories: (filters: unknown) => ["assets", "categories", filters] as const,
    list: (filters: unknown) => ["assets", "list", filters] as const,
    detail: (id: string) => ["assets", "detail", id] as const,
    my: ["assets", "my"] as const,
    assignments: (filters: unknown) => ["assets", "assignments", filters] as const,
    transfers: (filters: unknown) => ["assets", "transfers", filters] as const,
    maintenance: (filters: unknown) => ["assets", "maintenance", filters] as const,
    returns: (assetId: string) => ["assets", "returns", assetId] as const,
    exitClearances: (filters: unknown) => ["assets", "exitClearances", filters] as const,
    exitClearance: (id: string) => ["assets", "exitClearance", id] as const,
    warrantyExpiring: (days?: number) => ["assets", "warrantyExpiring", days] as const,
    dashboard: ["assets", "dashboard"] as const,
    reportSummary: ["assets", "reportSummary"] as const,
    requests: (filters: unknown) => ["assets", "requests", filters] as const,
    request: (id: string) => ["assets", "request", id] as const,
    incidents: (filters: unknown) => ["assets", "incidents", filters] as const,
    incident: (id: string) => ["assets", "incident", id] as const,
  },
  workflow: {
    definitions: (filters: unknown) => ["workflow", "definitions", filters] as const,
    definition: (id: string) => ["workflow", "definition", id] as const,
    instance: (id: string) => ["workflow", "instance", id] as const,
    myApprovals: (filters: unknown) => ["workflow", "myApprovals", filters] as const,
    myDelegations: ["workflow", "myDelegations"] as const,
  },
  performance: {
    dashboard: ["performance", "dashboard"] as const,
    cycles: (filters: unknown) => ["performance", "cycles", filters] as const,
    cycle: (id: string) => ["performance", "cycle", id] as const,
    cycleProgress: (id: string, params: unknown) => ["performance", "cycleProgress", id, params] as const,
    myReview: (cycleId: string) => ["performance", "myReview", cycleId] as const,
    employeeReview: (cycleId: string, employeeId: string) => ["performance", "employeeReview", cycleId, employeeId] as const,
    goals: (filters: unknown) => ["performance", "goals", filters] as const,
    goal: (id: string) => ["performance", "goal", id] as const,
    reviews: (filters: unknown) => ["performance", "reviews", filters] as const,
    review: (id: string) => ["performance", "review", id] as const,
    feedbackRequests: (filters: unknown) => ["performance", "feedbackRequests", filters] as const,
    myFeedbackTasks: (params: unknown) => ["performance", "myFeedbackTasks", params] as const,
    feedbackRequest: (id: string) => ["performance", "feedbackRequest", id] as const,
    ratingScales: ["performance", "ratingScales"] as const,
    pips: (filters: unknown) => ["performance", "pips", filters] as const,
    pip: (id: string) => ["performance", "pip", id] as const,
    developmentPlans: (filters: unknown) => ["performance", "developmentPlans", filters] as const,
    developmentPlan: (id: string) => ["performance", "developmentPlan", id] as const,
    reviewCompletionReport: (cycleId: string) => ["performance", "reports", "reviewCompletion", cycleId] as const,
    ratingDistributionReport: (cycleId?: string) => ["performance", "reports", "ratingDistribution", cycleId] as const,
    departmentReport: (cycleId?: string) => ["performance", "reports", "department", cycleId] as const,
  },
  callAnalytics: {
    dashboard: (filters: unknown) => ["callAnalytics", "dashboard", filters] as const,
    logs: (filters: unknown) => ["callAnalytics", "logs", filters] as const,
    log: (id: string) => ["callAnalytics", "log", id] as const,
    telecallers: (filters: unknown) => ["callAnalytics", "telecallers", filters] as const,
    dailyReport: (filters: unknown) => ["callAnalytics", "dailyReport", filters] as const,
    employeeReport: (filters: unknown) => ["callAnalytics", "employeeReport", filters] as const,
    devices: (params: unknown) => ["callAnalytics", "devices", params] as const,
    settings: ["callAnalytics", "settings"] as const,
  },
  helpdesk: {
    categories: (filters: unknown = {}) => ["helpdesk", "categories", filters] as const,
    list: (filters: unknown) => ["helpdesk", "list", filters] as const,
    summary: (filters: unknown) => ["helpdesk", "summary", filters] as const,
    detail: (id: string) => ["helpdesk", "detail", id] as const,
    // ---- Phase 3: HR Operations ----
    operations: ["helpdesk", "operations"] as const,
    workload: (role?: string) => ["helpdesk", "workload", role] as const,
    slaPolicies: (filters: unknown) => ["helpdesk", "slaPolicies", filters] as const,
    assignmentRules: (filters: unknown) => ["helpdesk", "assignmentRules", filters] as const,
    escalationRules: (filters: unknown) => ["helpdesk", "escalationRules", filters] as const,
    // ---- Phase 4: Knowledge & Productivity ----
    knowledgeCategories: (filters: unknown) => ["helpdesk", "knowledgeCategories", filters] as const,
    knowledgeArticles: (filters: unknown) => ["helpdesk", "knowledgeArticles", filters] as const,
    managedArticles: (filters: unknown) => ["helpdesk", "managedArticles", filters] as const,
    popularArticles: ["helpdesk", "popularArticles"] as const,
    recentArticles: ["helpdesk", "recentArticles"] as const,
    articleDetail: (id: string) => ["helpdesk", "articleDetail", id] as const,
    articleVersions: (id: string) => ["helpdesk", "articleVersions", id] as const,
    knowledgeFAQs: (filters: unknown) => ["helpdesk", "knowledgeFAQs", filters] as const,
    managedFAQs: (filters: unknown) => ["helpdesk", "managedFAQs", filters] as const,
    faqDetail: (id: string) => ["helpdesk", "faqDetail", id] as const,
    ticketKnowledgeLinks: (ticketId: string) => ["helpdesk", "ticketKnowledgeLinks", ticketId] as const,
    cannedResponses: (filters: unknown) => ["helpdesk", "cannedResponses", filters] as const,
    // ---- Phase 5: Analytics & Employee Experience ----
    rating: (ticketId: string) => ["helpdesk", "rating", ticketId] as const,
    analytics: {
      summary: (params: unknown) => ["helpdesk", "analytics", "summary", params] as const,
      volume: (params: unknown) => ["helpdesk", "analytics", "volume", params] as const,
      status: (params: unknown) => ["helpdesk", "analytics", "status", params] as const,
      categories: (params: unknown) => ["helpdesk", "analytics", "categories", params] as const,
      priorities: (params: unknown) => ["helpdesk", "analytics", "priorities", params] as const,
      workload: (params: unknown) => ["helpdesk", "analytics", "workload", params] as const,
      responseTime: (params: unknown) => ["helpdesk", "analytics", "responseTime", params] as const,
      resolutionTime: (params: unknown) => ["helpdesk", "analytics", "resolutionTime", params] as const,
      sla: (params: unknown) => ["helpdesk", "analytics", "sla", params] as const,
      escalations: (params: unknown) => ["helpdesk", "analytics", "escalations", params] as const,
      knowledge: (params: unknown) => ["helpdesk", "analytics", "knowledge", params] as const,
      cannedResponses: (params: unknown) => ["helpdesk", "analytics", "cannedResponses", params] as const,
      satisfaction: (params: unknown) => ["helpdesk", "analytics", "satisfaction", params] as const,
    },
  },
  // Send email (HR composing one message to employees). `sent` is the history
  // list, `detail` one sent email with each recipient's delivery.
  emailCampaigns: {
    options: ["emailCampaigns", "options"] as const,
    preview: (payload: unknown) => ["emailCampaigns", "preview", payload] as const,
    sent: (filters: unknown) => ["emailCampaigns", "sent", filters] as const,
    detail: (id: string) => ["emailCampaigns", "detail", id] as const,
  },
  tasks: {
    list: (filters: unknown) => ["tasks", "list", filters] as const,
    detail: (id: string) => ["tasks", "detail", id] as const,
    boards: ["tasks", "boards"] as const,
    monitor: (filters: unknown) => ["tasks", "monitor", filters] as const,
  },
};
